/**
 * Audio engine: context lifecycle, buses, master chain.
 *
 * Graph (Phase 6):
 *
 *   music sources -> musicGain -> musicDuck -.
 *                                             +-> masterGain -> limiter -> destination
 *   voice buffer  -> voiceGain ---------------'
 *
 * One AudioContext for the page lifetime. The context is created or resumed
 * inside the user gesture; the limiter sits at the very end so summed voice
 * plus overlapping music cannot clip (AC.10).
 */

export interface EngineNodes {
  context: AudioContext;
  musicGain: GainNode;
  musicDuck: GainNode;
  voiceGain: GainNode;
  masterGain: GainNode;
  limiter: DynamicsCompressorNode;
}

/**
 * Music ceiling, applied at the top of the music slider.
 *
 * This is a *ceiling*, not the default mix. The slider resolves to
 * `MIN + position * (GAIN - MIN)`, so a listener at 100% genuinely hears the bed
 * at full strength. Previously the slider multiplied this constant directly,
 * which made 100% equal the intended background level — there was no way to ask
 * for more, which read as a broken volume control.
 *
 * Measured with the bed's glue compression: at this ceiling the bed renders
 * around -14 dBFS RMS (-20 under the voice duck), up from about -22.
 */
export const MUSIC_BASE_GAIN = 0.8;
/** Silent end of the music slider. Not zero, so the bed still fades rather than cuts. */
export const MUSIC_MIN_GAIN = 0.0001;
export const VOICE_BASE_GAIN = 0.85;
export const VOICE_MIN_GAIN = 0.0001;
export const MASTER_GAIN = 0.9;

/** Map a 0..1 slider position onto [min, max]. */
function sliderGain(position: number, max: number, min: number): number {
  return min + clamp01(position) * (max - min);
}

export function createLimiter(context: BaseAudioContext): DynamicsCompressorNode {
  const limiter = context.createDynamicsCompressor();
  // A hard-ish limiter: high ratio, low threshold, fast attack.
  limiter.threshold.value = -3;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.12;
  return limiter;
}

export function connectEngine(
  context: AudioContext,
  destination: AudioNode = context.destination,
): EngineNodes {
  const musicGain = context.createGain();
  const musicDuck = context.createGain();
  const voiceGain = context.createGain();
  const masterGain = context.createGain();
  const limiter = createLimiter(context);

  musicGain.gain.value = MUSIC_BASE_GAIN;
  musicDuck.gain.value = 1;
  voiceGain.gain.value = VOICE_BASE_GAIN;
  masterGain.gain.value = MASTER_GAIN;

  musicGain.connect(musicDuck);
  musicDuck.connect(masterGain);
  voiceGain.connect(masterGain);
  masterGain.connect(limiter);
  limiter.connect(destination);

  return { context, musicGain, musicDuck, voiceGain, masterGain, limiter };
}

export class AudioEngine {
  private nodes: EngineNodes | null = null;
  private voiceLevel = VOICE_BASE_GAIN;
  private musicLevel = MUSIC_BASE_GAIN;

  get context(): AudioContext | null {
    return this.nodes?.context ?? null;
  }

  get buses(): EngineNodes | null {
    return this.nodes;
  }

  /** Must be called from a user gesture the first time. Idempotent. */
  async ensure(): Promise<EngineNodes> {
    if (!this.nodes) {
      const context = new AudioContext({ latencyHint: "playback" });
      this.nodes = connectEngine(context);
      this.applyLevels();
    }
    if (this.nodes.context.state !== "running") {
      await this.nodes.context.resume();
    }
    return this.nodes;
  }

  /** 0..1 from the voice slider. */
  setVoiceVolume(value: number): void {
    this.voiceLevel = sliderGain(value, VOICE_BASE_GAIN, VOICE_MIN_GAIN);
    this.applyLevels();
  }

  /** 0..1 from the music slider. */
  setMusicVolume(value: number): void {
    this.musicLevel = sliderGain(value, MUSIC_BASE_GAIN, MUSIC_MIN_GAIN);
    this.applyLevels();
  }

  get voiceBusLevel(): number {
    return this.voiceLevel;
  }

  get musicBusLevel(): number {
    return this.musicLevel;
  }

  async suspend(): Promise<void> {
    if (this.nodes && this.nodes.context.state === "running") {
      await this.nodes.context.suspend();
    }
  }

  async resume(): Promise<void> {
    if (this.nodes && this.nodes.context.state === "suspended") {
      await this.nodes.context.resume();
    }
  }

  async dispose(): Promise<void> {
    const nodes = this.nodes;
    this.nodes = null;
    if (!nodes) return;
    try {
      nodes.musicGain.disconnect();
      nodes.musicDuck.disconnect();
      nodes.voiceGain.disconnect();
      nodes.masterGain.disconnect();
      nodes.limiter.disconnect();
    } catch {
      // already disconnected
    }
    if (nodes.context.state !== "closed") {
      await nodes.context.close();
    }
  }

  private applyLevels(): void {
    if (!this.nodes) return;
    this.nodes.voiceGain.gain.value = this.voiceLevel;
    this.nodes.musicGain.gain.value = this.musicLevel;
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** Convenience for tests and the player: current context time, or 0. */
export function currentTime(context: BaseAudioContext | null): number {
  return context ? context.currentTime : 0;
}
